import { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import BackButton from '@lib/core/src/components/BackButton';
import Button from '@lib/core/src/components/Button';
import TextInput from '@lib/core/src/components/TextInput';
import palette from '@lib/core/src/theme/palette';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Stack } from '@mui/material';
import {
  Container,
  InputAndTitleContainer,
  InputContainer,
  InputError,
  InputsContainer,
  InputTitle,
  Title,
  TitleDescription,
  TitlesContainer
} from '@pages/LoginFlow/ForgotPassword/ForgotPasswordStyles';

import { useResetPassword } from '@/api/generated';
import RoutesMap from '@/enums/RoutesMap';

import ResetSuccess from './ResetSuccess';

const ResetPassword = () => {
  // Define keys for search parameters
  const confirmationTokenKey = 'confirmationToken';

  // Use the reset password mutation
  const {
    mutateAsync: authReset,
    isPending,
    isError: resetError,
    isSuccess: resetSuccess
  } = useResetPassword();

  // Get search parameters from the URL
  const [searchParams] = useSearchParams();
  const confirmationToken = searchParams.get(confirmationTokenKey);

  const navigate = useNavigate();

  // State variables for password and confirmation password
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Validate the confirmation token
  const isValidToken = confirmationToken && /^[A-Za-z0-9-_=.]+$/.test(confirmationToken);

  // Check if the form is valid
  const isValidForm =
    !passwordError && password && !confirmPasswordError && confirmPassword && isValidToken;

  const handlePasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setPassword(value);
      const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$_!%*#?&]{8,}$/;
      if (value && !passwordRegex.test(value)) {
        setPasswordError(
          'Password must be at least 8 characters long and include at least one letter and one number'
        );
      } else {
        setPasswordError('');
      }
    },
    [setPassword, setPasswordError]
  );

  const handleConfirmPasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setConfirmPassword(value);
      if (value !== password) {
        setConfirmPasswordError('Passwords do not match');
      } else {
        setConfirmPasswordError('');
      }
    },
    [password, setConfirmPassword, setConfirmPasswordError]
  );

  const toggleShowPassword = useCallback(() => {
    setShowPassword(prevShowPassword => !prevShowPassword);
  }, []);

  const toggleShowConfirmPassword = useCallback(() => {
    setShowConfirmPassword(prevShowConfirmPassword => !prevShowConfirmPassword);
  }, []);

  const onSubmit = async () => {
    if (!isValidForm) return;
    authReset({
      data: {
        newPassword: password,
        confirmationToken: confirmationToken!
      }
    });
  };

  return resetSuccess ? (
    <ResetSuccess />
  ) : (
    <Container>
      <TitlesContainer>
        <Title>Reset your password</Title>
        <TitleDescription>Please pick a new password</TitleDescription>
      </TitlesContainer>
      <InputsContainer>
        <InputAndTitleContainer>
          <InputTitle>Password</InputTitle>
          <Stack>
            <InputContainer>
              <TextInput
                type={showPassword ? 'text' : 'password'}
                placeholder="Set password"
                value={password}
                onChange={handlePasswordChange}
              />
              <span
                onClick={toggleShowPassword}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '8px',
                  zIndex: 15
                }}
              >
                {showPassword ? (
                  <VisibilityOffOutlinedIcon
                    sx={{ cursor: 'pointer', width: '14px', color: palette.common.color.grey1 }}
                  />
                ) : (
                  <VisibilityOutlinedIcon
                    sx={{ cursor: 'pointer', width: '14px', color: palette.common.color.grey1 }}
                  />
                )}
              </span>
            </InputContainer>
            {passwordError && <InputError>{passwordError}</InputError>}
          </Stack>
        </InputAndTitleContainer>
        <InputAndTitleContainer>
          <InputTitle>Confirm Password</InputTitle>
          <Stack>
            <InputContainer>
              <TextInput
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
              />
              <span
                onClick={toggleShowConfirmPassword}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '8px',
                  zIndex: 15
                }}
              >
                {showPassword ? (
                  <VisibilityOffOutlinedIcon
                    sx={{ cursor: 'pointer', width: '14px', color: palette.common.color.grey1 }}
                  />
                ) : (
                  <VisibilityOutlinedIcon
                    sx={{ cursor: 'pointer', width: '14px', color: palette.common.color.grey1 }}
                  />
                )}
              </span>
            </InputContainer>
            {confirmPasswordError && !passwordError && (
              <InputError>{confirmPasswordError}</InputError>
            )}
          </Stack>
        </InputAndTitleContainer>
      </InputsContainer>
      <Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap="16px">
          <BackButton onClick={() => navigate(RoutesMap.Login)} />
          <Button
            text="Reset my password"
            onClick={() => onSubmit()}
            loading={isPending}
            disabled={!isValidForm}
          />
        </Stack>
        {resetError && (
          <InputError>
            Oops.. There was an error reseting your password, please try again or contact support.
          </InputError>
        )}
      </Stack>
    </Container>
  );
};

export default ResetPassword;
